import React, { useMemo, useRef } from 'react';

import {
  MultiLineChartLine,
  TooltipData,
  TooltipSort,
} from '../../MultiLineChart.types';
import * as S from './MultiLineChartTooltip.styles';

export type MultiLineChartTooltipProps = {
  lines: MultiLineChartLine[];
  tooltipData: TooltipData;
  formatter: (n: number) => string;
  sortBy?: TooltipSort;
  offsetY?: number;
  notAvailableLabel: string;
  locale?: string;
  isBreakdown?: boolean;
};

const MIN_Y_VALUE_FOR_BREAKDOWN = 12;

export const MultiLineChartTooltip = ({
  lines,
  tooltipData,
  formatter,
  sortBy,
  offsetY = 0,
  notAvailableLabel,
  locale,
  isBreakdown = false,
}: MultiLineChartTooltipProps) => {
  const minYRef = useRef<number | undefined>(undefined);

  const shiftOptions = useMemo(
    () => ({
      mainAxis: false,
      crossAxis: true,
      limiter: {
        fn: (state: { x: number; y: number }) => {
          const { y } = state;

          const numberOfAssets = lines.length;

          if (minYRef.current === undefined) {
            minYRef.current = y;
          }

          const yValueToUse =
            isBreakdown && numberOfAssets >= 8 ? MIN_Y_VALUE_FOR_BREAKDOWN : y;

          return {
            x: state.x,
            y: yValueToUse,
          };
        },
      },
    }),
    []
  );

  const tooltipRows = lines.map(line => ({
    color: line.color,
    label: line.label,
    value: line.data.find(d => d.date.valueOf() === tooltipData.date.valueOf())
      ?.value,
    isBenchmark: line.isBenchmark,
  }));

  if (sortBy) {
    tooltipRows.sort((a, b) =>
      sortBy === 'desc'
        ? (b.value ?? 0) - (a.value ?? 0)
        : (a.value ?? 0) - (b.value ?? 0)
    );
  }

  const formatDate = (date: Date) =>
    date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const TooltipLabel = (
    <S.TooltipLabelContainer data-testid="multi-line-chart-tooltip">
      <S.DateLabel>{formatDate(tooltipData.date)}</S.DateLabel>
      {tooltipRows.map(row => (
        <S.TooltipRow key={row.label} data-testid="tooltip-row">
          <S.TooltipLegend>
            <S.TooltipLegendColor
              $color={row.color}
              $dashed={row.isBenchmark}
            />
            <div>{row.label}</div>
          </S.TooltipLegend>
          <div data-testid="row-value-label">
            {row.value !== undefined ? formatter(row.value) : notAvailableLabel}
          </div>
        </S.TooltipRow>
      ))}
    </S.TooltipLabelContainer>
  );

  return (
    <S.StyledTooltip
      inPortal
      label={TooltipLabel}
      isTouchDevice={false}
      isOpen
      hasArrow={false}
      offsetY={offsetY}
      dismissible={false}
      shiftOptions={shiftOptions}
      strategy="fixed"
    >
      <S.TooltipRoot style={{ left: `${tooltipData.xPos}px` }} />
    </S.StyledTooltip>
  );
};
